import { useCallback } from 'react';
import { createFileRoute, Outlet } from '@tanstack/react-router';

import type { AdminUserSort, DepartmentSort, SortDirection } from '~/api/types';
import { USER_ROLES } from '~/features/auth/roles';
import { AdminUsersScreen } from '~/features/customerAdministration/users/AdminUsersScreen';
import { CUSTOMER_ADMIN_USERS_TABS } from '~/features/customerAdministration/users/constants';
import { CustomerAdminUsersSearchParamsProvider } from '~/features/customerAdministration/users/params';
import type {
  CustomerAdminUsersSearchParams,
  CustomerAdminUsersSearchParamsHandler,
} from '~/features/customerAdministration/users/types';
import { protectedRoute } from '~/utils/routesAuthorization';

export const Route = createFileRoute('/customer-administration/users')({
  component: RouteComponent,
  validateSearch: (search): CustomerAdminUsersSearchParams => {
    // Validate and parse the search params into a typed state
    const defaultSortDir: SortDirection =
      search.sortDir === 'asc' ? 'asc' : 'desc';
    const sortDir = search.sortDir ? defaultSortDir : undefined;
    const filters = search.filters ?? undefined;

    return {
      page: search.page ? Number(search.page) || 1 : undefined,
      sort:
        (search.sort as AdminUserSort | DepartmentSort | undefined) ??
        undefined,
      search:
        search.search && typeof search.search === 'string'
          ? decodeURI(search.search)
          : undefined,
      sortDir,
      filters,
      tab:
        (search.tab as CustomerAdminUsersSearchParams['tab']) ??
        CUSTOMER_ADMIN_USERS_TABS.all,
    };
  },
  beforeLoad: () => {
    protectedRoute({
      roles: [USER_ROLES.AdminAbbott, USER_ROLES.AbbottUser],
    });
  },
});

function RouteComponent() {
  const navigate = Route.useNavigate();

  const onSearchParamsChange: CustomerAdminUsersSearchParamsHandler =
    useCallback((onSearch) => navigate({ search: onSearch }), [navigate]);

  return (
    <CustomerAdminUsersSearchParamsProvider
      params={Route.useSearch()}
      onSearchParamsChange={onSearchParamsChange}
    >
      <AdminUsersScreen>
        <Outlet />
      </AdminUsersScreen>
    </CustomerAdminUsersSearchParamsProvider>
  );
}
